import get from 'lodash/get';
import { useStatusesStore } from '@/stores/statuses.store';
import { useVocLabelsStore } from '@/stores/voc-labels.store';
import { useVocPortalStore } from '@/stores/voc-portal.store';
import { useVocPollWizardStore } from '@/stores/voc-poll-wizard.store';
import { useVocItemsStore } from '@/stores/voc-items.store';
import { useGroupsStore } from '@/stores/groups.store';
import { $canSub } from '@pendo/permissions';
import sanitizeHtml from 'sanitize-html';
import { NO_CATEGORY_STRING } from '@/voc/constants/constants';
import isArray from 'lodash/isArray';
import { AppsService } from '@/common/apps/apps.service';
import { formatTextWithNewLines } from '@/utils/voc-portal-shared.utils';
import { SOURCES, SURVEY_TYPE_SOURCES, SOURCE_CATEGORIES, SOURCE_DISPLAY_CATEGORIES, ALL_PORTALS_NAME } from '@/voc/constants/feedback';
import isString from 'lodash/isString';
import moment from 'moment';
import { getActiveSubscription } from '@/data/session/session-globals';
import { hasSegmentFlag, localSegmentFlagsExist } from '@/data/flags/local-flags';
import { activeSubHasPurchasedProductLine as hasProductLine } from '@/data/flags/product-lines';
import dayjs from 'dayjs';

export function formatProductAreas (store, productAreas) {
    if (!productAreas) return [];

    if (!isArray(productAreas)) {
        productAreas = [productAreas];
    }

    return productAreas.map((productArea) => {
        const group = useGroupsStore().groupById(productArea);
        if (!group) return null;

        const formattedProductArea = { ...group };
        delete formattedProductArea.type;

        return formattedProductArea;
    }).filter(Boolean);
}

export function getCreateIdeaTooltip (_store) {
    const toolTipConfig = {
        visible: false,
        content: '',
        arrow: true,
        placement: 'bottom',
    };

    if (!useVocItemsStore().canEditVocItems) {
        toolTipConfig.visible = true;
        toolTipConfig.content = 'To create ideas, ask your admin for Listen Manager permissions.';
    } else if (!useVocItemsStore().listenAppOptions.length) {
        toolTipConfig.visible = true;
        toolTipConfig.content = 'To create ideas, at least 1 installed app is required.';
    }

    return toolTipConfig;
}

export function getValidateIdeasTooltip (_store) {
    const toolTipConfig = {
        visible: false,
        content: '',
        arrow: true,
        placement: 'bottom',
    };

    const { vocItemCount } = useVocItemsStore();
    const vocPollWizardStore = useVocPollWizardStore();
    const { canCreatePoll } = vocPollWizardStore;
    const { hasCreateEditAppPerm } = vocPollWizardStore;

    if (vocItemCount === 0 && canCreatePoll && hasCreateEditAppPerm) {
        toolTipConfig.visible = true;
        toolTipConfig.content =
            "To start using idea tests you need at least one\nidea in your subscription.\n\nHit the 'Create idea' button to get started!";
    } else if (canCreatePoll && !hasCreateEditAppPerm) {
        toolTipConfig.visible = true;
        toolTipConfig.content =
            'To start using idea tests you need guides\npermission for at least one installed app\non this subscription.';
    } else if (!canCreatePoll) {
        toolTipConfig.visible = true;
        toolTipConfig.content = 'To start validating ideas, ask your admin for Listen Manager permissions.';
    }

    return toolTipConfig;
}

export function getSubmitFeedbackTooltip (_store) {
    const toolTipConfig = {
        visible: false,
        content: '',
        arrow: true,
        placement: 'bottom',
    };

    if (!useVocItemsStore().canSubmitFeedback) {
        toolTipConfig.visible = true;
        toolTipConfig.content =
            'To submit feedback, ask your admin for Validate Contributor or Listen Manager permissions.';
    }

    return toolTipConfig;
}

export function getAddVoteTooltip (_store) {
    const toolTipConfig = {
        visible: false,
        content: '',
        arrow: true,
        placement: 'bottom',
    };

    if (!useVocItemsStore().canAddVotes) {
        toolTipConfig.visible = true;
        toolTipConfig.content = 'To add votes, ask your admin for Listen Contributor or Listen Manager permissions.';
    }

    return toolTipConfig;
}

export function getSizeFromBytes (bytes) {
    if (bytes < 1024) {
        return `${bytes} bytes`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(2)} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export async function downloadImage ({ url, type, name }) {
    if (typeof window === 'undefined') return; // make sure we are in the browser
    const response = await fetch(url);
    const data = await response.blob();
    const metadata = {
        type: type || 'image',
    };

    return new File([data], name || url, metadata);
}

export function isGuideAnIdeaTest (guide) {
    if (guide?.polls && Array.isArray(guide.polls)) {
        return guide.polls.some((poll) => get(poll, 'attributes.type') === 'Vote');
    }

    return false;
}

export function canEditIdeaTest () {
    return $canSub()
        .entity('ideaPoll')
        .action('create');
}

export function formatLabels (labels) {
    // create array of categories, each with array of associated labels
    const categories = {};

    for (const element of labels) {
        const label = element;
        const category = label.category.name || NO_CATEGORY_STRING;

        if (!categories[category]) {
            if (category === NO_CATEGORY_STRING) {
                categories[category] = {
                    id: 'no-category',
                    category,
                    labels: [],
                    type: 'metaGroup',
                };
            } else {
                categories[category] = {
                    id: label.category.id,
                    category: label.category.name,
                    labels: [],
                    type: 'metaGroup',
                };
            }
        }

        if (!categories[category].labels.includes(label)) {
            categories[category].labels.push({
                id: label.id,
                label: label.name,
                categoryId: label.category.id,
                category,
                associatedItemCount: label.associatedItemCount,
            });
        }
    }

    const categoryArray = Object.values(categories);

    return sortCategoriesAndLabels(categoryArray);
}

export function sortCategoriesAndLabels (categories) {
    // sort categories by category name, with numbers and 'No Category' at the end
    categories.sort((a, b) => {
        if (a.category === NO_CATEGORY_STRING) {
            return 1;
        }
        if (b.category === NO_CATEGORY_STRING) {
            return -1;
        }

        if (!isNaN(a.category) && !isNaN(b.category)) {
            return a.category - b.category;
        }
        if (!isNaN(a.category)) {
            return 1;
        }
        if (!isNaN(b.category)) {
            return -1;
        }

        return a.category.localeCompare(b.category);
    });

    // sort labels within each category, with numbers at the end
    return categories.map((category) => {
        category.labels.sort((a, b) => {
            if (!isNaN(a.label) && !isNaN(b.label)) {
                return a.label - b.label;
            }
            if (!isNaN(a.label)) {
                return 1;
            }
            if (!isNaN(b.label)) {
                return -1;
            }

            return a.label.localeCompare(b.label);
        });

        return category;
    });
}

export function flattenLabels (labels) {
    return labels.reduce((acc, category) => {
        return acc.concat(category.labels);
    }, []);
}

export function formatLabelsForDownload (store, labelIds) {
    if (!labelIds) {
        return null;
    }

    const { labelOptions } = useVocLabelsStore();
    const filteredLabels = labelOptions.filter((label) => labelIds.includes(label.id));

    return flattenLabels(formatLabels(filteredLabels))
        .map((label) => `${label.category}: ${label.label}`)
        .join(', ');
}

export function findOptionEqualToInput (options, input) {
    if (!options.length) return undefined;
    if (options[0].label) {
        return options.find((option) => option.label.toLowerCase() === input.toLowerCase());
    }

    return options.find((option) => option.category.toLowerCase() === input.toLowerCase());
}

export function parseLabelInput (input) {
    if (input && input.includes(':')) {
        const [category, label] = input.split(':').map((str) => str.trim());

        return {
            category,
            label,
        };
    }

    return {
        label: input?.trim(),
    };
}

export function formatLabelsForRequest (labels) {
    return labels.map((label) => {
        if (label.id) return { id: label.id };
        if (label.categoryId && !label.categoryId.startsWith('new-category-')) {
            return { categoryId: label.categoryId, name: label.label };
        }
        if (label.category !== NO_CATEGORY_STRING) return { category: label.category, name: label.label };

        return { name: label.label };
    });
}

export function closeTableElementDropdown (isActive, el) {
    if (isActive && el) {
        el.$refs.core.deactivate();
    }
}

export function getOnBehalfOfDisplayableName (vocItem) {
    if (vocItem.onBehalfOf?.type === 'Visitor') {
        if (vocItem.onBehalfOf?.visitor?.metadata?.agent?.email) {
            return vocItem.onBehalfOf.visitor.metadata.agent.email;
        }

        if (vocItem.onBehalfOf?.visitor?.id) {
            return vocItem.onBehalfOf.visitor.id;
        }

        if (vocItem.onBehalfOf?.username) {
            return vocItem.onBehalfOf.username;
        }

        return vocItem.onBehalfOf?.id;
    }

    if (vocItem.onBehalfOf?.type === 'User') {
        if (vocItem.onBehalfOf?.user) {
            return `${vocItem.onBehalfOf.user.first} ${vocItem.onBehalfOf.user.last}`;
        }

        return vocItem.onBehalfOf?.username;
    }

    if (vocItem.createdBy?.user) {
        return `${vocItem.createdBy.user.first} ${vocItem.createdBy.user.last}`;
    }

    return vocItem.createdBy?.username;
}

export function formatFavorites (savedViews) {
    if (!savedViews) return [];
    const favorites = savedViews.filter((view) => view.starred) || [];
    const sortedFavorites = favorites.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

    return sortedFavorites.slice(0, 20);
}

export function formatAISummaryForEditor (summary, format = 'bulletPoints') {
    let content = [
        {
            type: 'paragraph',
            attrs: {
                textAlign: 'left',
            },
            content: [
                {
                    type: 'text',
                    text: summary.content,
                },
            ],
        },
    ];

    if (format === 'paragraph' && summary.firstTheme && summary.secondTheme) {
        const contentSections = [];
        const combinedRegex = new RegExp(`\\b${summary.firstTheme}|\\b${summary.secondTheme}`, 'ig');

        while (summary.content.search(combinedRegex) !== -1) {
            const match = summary.content.match(combinedRegex)[0];
            const matchIndex = summary.content.search(combinedRegex);
            const matchLength = match.length;
            const matchStart = summary.content.slice(0, matchIndex);
            const matchEnd = summary.content.slice(matchIndex + matchLength);
            const matchContent = summary.content.slice(matchIndex, matchIndex + matchLength);

            if (matchStart) {
                contentSections.push({
                    type: 'text',
                    text: matchStart,
                });
            }
            contentSections.push({
                type: 'text',
                marks: [
                    {
                        type: 'bold',
                    },
                ],
                text: matchContent,
            });

            summary.content = matchEnd;
        }

        if (summary.content.length > 0) {
            contentSections.push({
                type: 'text',
                text: summary.content,
            });
        }

        content = [
            {
                type: 'paragraph',
                attrs: {
                    textAlign: 'left',
                },
                content: contentSections,
            },
        ];
    }

    if (format === 'bulletPoints') {
        const splitContentSections = summary.content.split('\n');
        // Remove instances of markdown styling tags
        const replaceRegex = new RegExp(/(?<!\w)-|[*#]/g);

        // Gemini sometimes handles the delimiter we use for requests in the prompt incorrectly and returns them
        // as part of the response
        const delimiterRegex = /^###(.*)/;
        const removeLeadingDelimiters = (string) => string.replace(delimiterRegex, '$1');
        const contentSections = splitContentSections.reduce((acc, cur) => {
            if (cur.length === 0 || removeLeadingDelimiters(cur).length === 0) {
                return acc;
            }
            const theme = cur
                .split(':')[0]
                .replaceAll(replaceRegex, '')
                .trim();
            const content = cur
                .split(':')[1]
                .replace(/\b(\w+)-([st])\b/g, "$1'$2")
                .trim();
            acc.push({
                theme,
                content,
            });

            return acc;
        }, []);

        content = [
            {
                type: 'bulletList',
                content: contentSections.map((section) => {
                    return {
                        type: 'listItem',
                        attrs: {
                            textAlign: 'left',
                        },
                        content: [
                            {
                                type: 'paragraph',
                                attrs: {
                                    textAlign: 'left',
                                },
                                content: [
                                    {
                                        type: 'text',
                                        marks: [
                                            {
                                                type: 'bold',
                                            },
                                        ],
                                        text: section.theme,
                                    },
                                    {
                                        type: 'text',
                                        text: ` - ${section.content}`,
                                    },
                                ],
                            },
                        ],
                    };
                }),
            },
        ];
    }

    if (format === 'json') {
        content = [];
        summary.content.forEach((ratingGroup) => {
            const ratingGroupKey = Object.keys(ratingGroup)[0];
            const headerSection = {
                type: 'heading',
                attrs: { level: 4 },
                content: [{ type: 'text', text: ratingGroupKey }],
            };
            const bulletListSection = {
                type: 'bulletList',
                content: ratingGroup[ratingGroupKey].length > 0 ? ratingGroup[ratingGroupKey].map((theme) => {
                    return {
                        type: 'listItem',
                        content: [
                            {
                                type: 'paragraph',
                                content: [
                                    {
                                        type: 'text',
                                        marks: [
                                            {
                                                type: 'bold',
                                            },
                                        ],
                                        text: theme.themeName,
                                    },
                                    {
                                        type: 'text',
                                        text: ` - ${theme.themeDescription}`,
                                    },
                                ],
                            },
                        ],
                    };
                }) : [{
                    type: 'listItem',
                    content: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    marks: [
                                        {
                                            type: 'bold',
                                        },
                                    ],
                                    text: 'No clear feedback',
                                },
                                {
                                    type: 'text',
                                    text: ' - No clear feedback was provided.',
                                },
                            ],
                        },
                    ],
                }],
            };

            content.push(headerSection);
            content.push(bulletListSection);
        });
    }

    return {
        type: 'doc',
        ...summary,
        content,
    };
};

export function formatHighlightedText (text = '') {
    if (!text) return '';
    if (typeof text !== 'string') return text;

    let formattedText = formatTextWithNewLines(text);

    // Replace tags with mark tags and surround unhiglighted text with span tags
    formattedText = formattedText.replace(
        /\[highlightTagStart\](.*?)\[highlightTagEnd\]/g,
        '</span><mark class="keyword-highlight">$1</mark><span>',
    );
    formattedText = `<span>${formattedText}</span>`;

    // Remove empty spans
    formattedText = formattedText.replace(/<span>\s*<\/span>/g, '');

    // Remove extra spaces from  around spans
    formattedText = formattedText.replace(/\s*<span>\s*/g, '<span>');
    formattedText = formattedText.replace(/\s*<\/span>\s*/g, '</span>');

    return formattedText;
}

export function formatHighlightedSingleFormData (formData, formDataIndex, highlightedText) {
    // If the content is a number or empty, return the plain content (this is not returned by Elasticsearch)
    if(!formData[formDataIndex].content || !isNaN(formData[formDataIndex].content)) {
        return formData[formDataIndex].content;
    }
    const thisDescAsLines = formData[formDataIndex].content.split('\n');
    const highlightsAsLines = highlightedText.split('\n');

    const formDataUpToIndex = formData.slice(0, formDataIndex);
    const linesToSkip = formDataUpToIndex.reduce((acc, item) => {
        // If the content is a number or empty, skip it (this is not returned by Elasticsearch)
        if(!item.content || !isNaN(item.content)) {
            return acc;
        }

        return acc + item.content.split('\n').length;
    }, 0);

    const toHighlight = highlightsAsLines.slice(linesToSkip, linesToSkip + thisDescAsLines.length).join('\n');

    return formatHighlightedText(toHighlight);
}

export function scrollIntoViewIfNeeded (targetElement, { extra = 0 } = {}) {
    const { marginTop } = getComputedStyle(targetElement);

    if (extra) {
        const extraMarginTop = parseInt(marginTop) + extra;
        targetElement.style.marginTop = `${extraMarginTop}px`;
    }

    targetElement.scrollIntoView({ behavior: 'smooth', block: 'end' });

    if (extra) {
        targetElement.style.marginTop = marginTop;
    }
}

export function formatSources (sourcesIn) {
    const sources = [...sourcesIn];
    const categories = {};

    sources.forEach((source) => {
        source = formatSource(source);

        if (!categories[source.displayCategory]) {
            categories[source.displayCategory] = {
                id: source.displayCategory,
                category: source.displayCategory,
                sources: [],
                categoryDisplayOrder: source.categoryDisplayOrder,
                type: 'metaGroup',
            };
        }

        categories[source.displayCategory].sources.push({
            id: `${source.sourceCategory}${source.source}${source.sourceIdentifier}`,
            ...source,
        });
    });

    insertAllPortals(categories);

    if (categories.Listen?.sources.find((source) => source.source === 'InternalFeedbackForm')) {
        categories.Listen.sources = categories.Listen.sources.filter((source) => source.source !== 'IdeaList');
    }

    return sortSources(Object.values(categories));
}

export function insertAllPortals (categories) {
    const portalCategory = categories[SOURCE_DISPLAY_CATEGORIES.Portals.id];
    if (!portalCategory) return;

    const allPortalsExists = portalCategory.sources.find((portalSource) => portalSource.label === ALL_PORTALS_NAME);

    if (!allPortalsExists) {
        const formatAllPortalSource = formatSource({
            sourceCategory: SOURCE_CATEGORIES.VisitorExperience.id,
            source: 'Portal',
            sourceIdentifier: '',
        });

        categories[SOURCE_DISPLAY_CATEGORIES.Portals.id].sources.push({
            id: 'VisitorExperiencePortal',
            ...formatAllPortalSource,
        });
    }
}

export function getSource (source) {
    if (!source?.source) return null;

    let sourceConfig;
    if (!SOURCE_CATEGORIES[source.sourceCategory]) {
        sourceConfig = SOURCES[source.sourceCategory];
    } else {
        sourceConfig = SOURCES[source.source];
    }

    if (SURVEY_TYPE_SOURCES[source.source]) {
        sourceConfig = SURVEY_TYPE_SOURCES[source.source];
    }

    return sourceConfig;
}

export function formatSource (source) {
    const sourceConfig = getSource(source);

    if (!sourceConfig) return source;

    const formattedLabel = sourceConfig.getFormattedLabel ? sourceConfig.getFormattedLabel(source) : sourceConfig.label;
    const formattedConfig = {
        displayCategory: sourceConfig.sourceDisplayCategory.id,
        categoryDisplayOrder: sourceConfig.sourceDisplayCategory.displayOrder,
        label: formattedLabel,
        sourceDisplayOrder: sourceConfig.displayOrder,
        icon: sourceConfig.icon,
        iconWithName: sourceConfig.iconWithName,
    };

    return {
        ...source,
        ...formattedConfig,
    };
}

export function sortSources (sourceCategories) {
    sourceCategories.sort((a, b) => (a.categoryDisplayOrder > b.categoryDisplayOrder ? 1 : -1));

    return sourceCategories.map((sourceCategory) => {
        sourceCategory.sources.sort((a, b) => {
            if (a.sourceDisplayOrder === b.sourceDisplayOrder) {
                if (b.label.startsWith('(Deleted')) {
                    return -1;
                }
                if (a.label.startsWith('(Deleted')) {
                    return 1;
                }

                if (b.label === ALL_PORTALS_NAME) {
                    return 1;
                }
                if (a.label === ALL_PORTALS_NAME) {
                    return -1;
                }

                return a.label.localeCompare(b.label);
            }

            return a.sourceDisplayOrder > b.sourceDisplayOrder ? 1 : -1;
        });

        return sourceCategory;
    });
}

export function waitForBladeToClose (selector, delay = 50, tries = 40) {
    const element = document.querySelector(`.${selector}.is-open`);

    if (!window[`__${selector}`]) {
        window[`__${selector}`] = 0;
        window[`__${selector}__delay`] = delay;
        window[`__${selector}__tries`] = tries;
    }

    function _search () {
        return new Promise((resolve) => {
            window[`__${selector}`]++;
            setTimeout(resolve, window[`__${selector}__delay`]);
        });
    }

    function _clear () {
        delete window[`__${selector}`];
        delete window[`__${selector}__delay`];
        delete window[`__${selector}__tries`];
    }

    if (element !== null) {
        if (window[`__${selector}`] >= window[`__${selector}__tries`]) {
            _clear();

            return Promise.resolve(false);
        }

        return _search().then(() => waitForBladeToClose(selector));
    }

    return new Promise((resolve) => {
        _clear();
        setTimeout(resolve, 220);
    });
}

export function waitForBladeToOpen (selector, delay = 50, tries = 20) {
    const element = document.querySelector(`.${selector}.is-open`);

    if (!window[`__${selector}`]) {
        window[`__${selector}`] = 0;
        window[`__${selector}__delay`] = delay;
        window[`__${selector}__tries`] = tries;
    }

    function _search () {
        return new Promise((resolve) => {
            window[`__${selector}`]++;
            setTimeout(resolve, window[`__${selector}__delay`]);
        });
    }

    function _clear () {
        delete window[`__${selector}`];
        delete window[`__${selector}__delay`];
        delete window[`__${selector}__tries`];
    }

    if (element === null) {
        if (window[`__${selector}`] >= window[`__${selector}__tries`]) {
            _clear();

            return Promise.resolve(false);
        }

        return _search().then(() => waitForBladeToOpen(selector));
    }

    return new Promise((resolve) => {
        _clear();
        setTimeout(resolve, 220);
    });
}

export function hasDiscoverViewerAccess () {
    return (
        (localSegmentFlagsExist() && !hasSegmentFlag('hideDiscover')) ||
        hasProductLine('voc') ||
        hasProductLine('roadmaps')
    );
}

export function formatProductAreasForDownload (store, productAreas) {
    if (!productAreas) return null;

    return formatProductAreas(store, productAreas)
        .map((productArea) => productArea.name)
        .sort()
        .join(', ');
}

export function formatLinkedIdeasForDownload (ideas) {
    if (!ideas) return null;

    return ideas.map((idea) => getIdeaPath(idea?.id)).join(', ');
}

export function formatAppsForDownload (store, appIds) {
    if (!appIds) return null;

    const apps = store.getters['apps/getAppsByIds'](appIds);

    return (
        apps
            .reduce((acc, app) => {
                if (app !== undefined) {
                    acc.push(app.name);
                }

                return acc;
            }, [])
            .join(', ') || null
    );
}

export function formatStatusForDownload (store, statusId) {
    if (!statusId) return null;

    return useStatusesStore().statusItemById(statusId)?.name ?? '--';
}

export function calculateLabelIndex (store, label, selectedLabels) {
    const { category, id } = label;

    if (!id) {
        return 1;
    }

    let { id: categoryId } = category;

    if (!categoryId) categoryId = 'no-category';

    const selectedLabelIds = selectedLabels.map((label) => label.id);
    const filteredLabels = useVocLabelsStore().labelOptions.filter((label) => !selectedLabelIds.includes(label.id));
    const categories = formatLabels(filteredLabels);
    const categoryIndex = categories.findIndex((category) => category.id === categoryId);

    if (categoryIndex === -1) {
        return 1;
    }

    const { labels } = categories[categoryIndex];
    const indexInCategory = labels.findIndex((label) => label.id === id);

    if (indexInCategory === -1) {
        return 1;
    }

    let index = 0;
    for (let i = 0; i < categoryIndex; i++) {
        index += categories[i].labels.length + 1;
    }
    index += indexInCategory + 1;

    return index;
}

export function calculateCategoryIndex (store, category, selectedLabels) {
    let { id: categoryId } = category;

    if (!categoryId) categoryId = 'no-category';

    const selectedLabelIds = selectedLabels.map((label) => label.id);
    const filteredLabels = useVocLabelsStore().labelOptions.filter((label) => !selectedLabelIds.includes(label.id));
    const categories = formatLabels(filteredLabels);
    const categoryIndex = categories.findIndex((category) => category.id === categoryId);

    if (categoryIndex === -1) {
        return 0;
    }

    let index = 0;
    for (let i = 0; i < categoryIndex; i++) {
        index += categories[i].labels.length + 1;
    }

    return index;
}

export function formatDescriptionForDownload (formData) {
    if (!formData) return null;

    const descriptionFormData = formData.slice(1) || [];

    return descriptionFormData
        .map((section) => {
            return `'${sanitizeHtml(section.title)}' '${sanitizeHtml(section.content)}'`;
        })
        .join(' - ');
}

export function formatPortalsForDownload (store, portalIds) {
    if (!portalIds || !portalIds.length) return '--';
    const vocPortalStore = useVocPortalStore();
    const portals = vocPortalStore.getPortalsByIds(portalIds);

    return (
        portals
            .map((portal) => portal?.internalName)
            .filter(Boolean)
            .join(', ') || null
    );
}

export function formatVocItemFilterForAgg (key, filter, returnEmpty = true) {
    if (!returnEmpty && isArray(filter) && !filter.length) {
        return null;
    }
    if (!returnEmpty && isString(filter) && !filter.trim()) {
        return null;
    }

    switch (key) {
        case 'appId':
        case 'appIds':
            if (filter?.length && filter[0] !== '*') {
                return {
                    key: 'appId',
                    value: filter.length === 1 ? AppsService.getAppAggIdsFromOption(filter[0]) : filter,
                };
            }

            return null;
        case 'feedbackStatuses':
            return {
                key: 'statuses',
                value: filter,
            };
        case 'segmentId':
            if (filter && filter !== 'everyone') {
                return {
                    key,
                    value: filter,
                };
            }

            return null;
    }

    return {
        key,
        value: filter,
    };
}

export function formatVocItemFiltersForAgg (
    filters,
    { returnEmpty = true, prompt, topics = [], vocInsightIds = [] } = { returnEmpty: true },
) {
    // vocInsightIds provided directly from backend if topics are hidden due to permissions
    if (vocInsightIds?.length > 0) {
        return { vocInsightIds };
    }

    if (prompt) {
        return {
            vocInsightIds: topics.reduce((ids, topic) => {
                topic.vocInsightIds.forEach((vocInsightId) => {
                    if (!ids.includes(vocInsightId)) {
                        ids.push(vocInsightId);
                    }
                });

                return ids;
            }, []),
        };
    }

    return Object.entries(filters).reduce((formattedFilters, [key, filter]) => {
        const formattedFilter = formatVocItemFilterForAgg(key, filter, returnEmpty);

        if (formattedFilter) {
            formattedFilters[formattedFilter.key] = formattedFilter.value;
        }

        return formattedFilters;
    }, {});
}

export function validateEmails (emails) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    return emails.filter((email) => emailRegex.test(email.recipientIdentifier));
}

export async function checkCanSendEmailUpdate (store) {
    await Promise.all([
        store.dispatch('subscription/loadSetting', { setting: 'visitorEmail', retrieveMetadata: false }),
        store.dispatch('emails/loadDomainIdentities'),
        store.dispatch('emails/loadProfiles', { kind: 'voc' }),
    ]);

    const verifiedDomains = store.getters['emails/getVerifiedDomains'];
    const defaultProfile = store.getters['emails/defaultProfile'];
    const { settings } = store.state.subscription;

    const hasDomainSetup = verifiedDomains.length > 0;
    const hasEmailMetadata = get(settings, 'visitorEmail.value', null) !== null;
    const hasSenderSettings = defaultProfile?.kind === 'voc';

    return { hasDomainSetup, hasEmailMetadata, hasSenderSettings };
}

export function formatEmbeddedCallUrl (sourceCategory, callUrlString) {
    if (sourceCategory === 'Gong') {
        return callUrlString?.replace('/call?id=', '/embedded-call?call-id=');
    } if (sourceCategory === 'Teams') {
        try {
            const urls = JSON.parse(callUrlString);
            if (Array.isArray(urls) && urls.length > 0) {
                return urls[0];
            }
        } catch {
            return callUrlString;
        }
    }

    return callUrlString;
}

export function formatTimestampForCallTranscript ({ timestamp, beginningOfSpeech }) {
    const timeDifference = timestamp - beginningOfSpeech;

    const oneHourInMs = 3600000; // 1 hour = 3600000 milliseconds
    const format = timeDifference >= oneHourInMs ? 'hh:mm:ss' : 'mm:ss';
    const formatted = moment.utc(timeDifference).format(format);

    return formatted;
}

export function formatCallTranscriptFormData (formData, createdAt, maxFormDataLength) {
    if (!formData || formData.length <= 1) return [];

    const splitFormData = formData.slice(1);
    const length = Math.min(maxFormDataLength || splitFormData.length, splitFormData.length);

    let currentSpeakerId = splitFormData[0].speaker.id;
    let currentFormDataItem = {
        ...splitFormData[0],
        title: createdAt ? callTranscriptFormDataTitle(splitFormData[0], createdAt) : splitFormData[0].title,
    };
    const formattedFormData = [];

    for (let i = 1; i < length; i++) {
        const item = splitFormData[i];
        if (item.speaker.id !== currentSpeakerId) {
            formattedFormData.push(currentFormDataItem);
            currentFormDataItem = {
                ...item,
                title: createdAt ? callTranscriptFormDataTitle(item, createdAt) : item.title,
            };
            currentSpeakerId = item.speaker.id;
        } else {
            currentFormDataItem.content += ` ${item.content}`;
        }
    }

    formattedFormData.push(currentFormDataItem);

    return formattedFormData;
}

function callTranscriptFormDataTitle (formDataItem, beginningOfSpeech) {
    const timestamp = formatTimestampForCallTranscript({
        timestamp: formDataItem.timestamp,
        beginningOfSpeech,
    });
    const speakerName = formDataItem.speaker.name || formDataItem.speaker.id || 'Unknown Speaker';

    return `${timestamp} ${speakerName}`;
}

export function formatChatTranscriptFormData (formData, maxFormDataLength) {
    if (!formData || formData.length <= 1) return [];

    const splitFormData = formData.slice(1);
    const length = Math.min(maxFormDataLength || splitFormData.length, splitFormData.length);

    const sliced = splitFormData
        .slice(0, length)
        .filter((item) => !!item && !!item.content);

    function chatTranscriptFormDataTitle (item) {
        const timestamp = item.timestamp ? formatTimestampForChatMessage(item.timestamp) : '';
        const speakerName = item.speaker.name || item.speaker.id || 'Unknown Speaker';

        return `${speakerName} ${timestamp}`.trim();
    }

    return sliced
        .map((item) => {
            const {
                content,
                timestamp,
                sourceIdentifier,
            } = item;

            const speaker = {
                id: (item.speaker && item.speaker.id) || item.id || null,
                name: (item.speaker && item.speaker.name) || item.name || item.id || null,
                type: (item.speaker && item.speaker.type) || null,
            };

            return {
                content,
                timestamp,
                speaker,
                sourceIdentifier,
                title: chatTranscriptFormDataTitle({ ...item, speaker }),
                type: speaker.type,
            };
        });
}

export function formatTimestampForChatMessage (timestamp) {
    return dayjs(timestamp).format('MMM D [at] h:mm A');
}

export async function getAccountTypeField ({ store, filters }) {
    const accountTypeField = store.state?.subscription?.settings?.accountType?.value || null;
    if (!accountTypeField && filters?.accountTypes?.length) {
        await store
            .dispatch('subscription/loadSetting', { setting: 'accountType', retrieveMetadata: false })
            .catch((error) => error);

        return store.state?.subscription?.settings?.accountType?.value || null;
    }

    return accountTypeField;
}

export function getIdeaPath (ideaId) {
    const query = ideaId ? `?openItem=${ideaId}` : '';

    return `${window.location.origin}/s/${getActiveSubscription()?.id}/validate${query}`;
}

/**
 * Processes statuses to add disabled property and sorts them with disabled items at the bottom
 *
 * @param {Array} options - Array of status option objects
 * @returns {Array} Processed and sorted array with disabled property
 */
export function processAndSortStatuses (options) {
    const processedOptions = options.map((option) => ({
        ...option,
        disabled: option.deletePending || false,
    }));

    return sortStatusesWithDisabledAtBottom(processedOptions);
}

/**
 * Sorts statuses with disabled items at the bottom
 *
 * @param {Array} statuses - Array of status objects with disabled and other status properties
 * @returns {Array} New sorted array with disabled statuses at the bottom
 */
export function sortStatusesWithDisabledAtBottom (statuses) {
    return [...statuses].sort((a, b) => {
        // If same disabled status, maintain original order
        if (a.disabled === b.disabled) {
            return a.displayOrder - b.displayOrder;
        }

        // Put disabled rows at the bottom
        return a.disabled ? 1 : -1;
    });
}

export function isSyncing (vocSourceConfig) {
    return (
        vocSourceConfig?.initSync?.status === 'in progress' ||
        vocSourceConfig?.initSync?.status === 'not started' ||
        vocSourceConfig?.continuousSync[0]?.status === 'in progress' ||
        vocSourceConfig?.continuousSync[0]?.status === 'not started'
    );
}

// These source names come from the backend and are not available for linking. This is not display label !
const UNAVAILABLE_NAMES = new Set([
    '(Unavailable Guide)',
    '(Unavailable NPS)',
    '(Unavailable Survey)',
    '(Deleted idea portal)',
]);

export function resolveFeedbackSourceLink (vocSource, possibleSources = []) {
    const noLink = { route: null, tooltipContent: '' };
    const sourceConfig = getSource(vocSource);

    if (!sourceConfig?.isLinkable) {
        return noLink;
    }

    if (sourceConfig.id === 'Portal') {
        const { sourceIdentifier } = vocSource;
        if (!sourceIdentifier) {
            return noLink;
        }

        const displayLabel = formatSource(vocSource).label;
        if (UNAVAILABLE_NAMES.has(displayLabel)) {
            return noLink;
        }

        return {
            route: { name: 'VocPortalSettingsTab', params: { portalId: sourceIdentifier } },
            tooltipContent: displayLabel || '',
        };
    }

    const objectId = vocSource.sourceIdentifier?.slice(0, 27) || '';
    if (!objectId) {
        return noLink;
    }

    const match = possibleSources.find((entry) => entry.sourceIdentifier === objectId);
    if (!match || UNAVAILABLE_NAMES.has(match.guideName)) {
        return noLink;
    }

    const guideName = match.guideName || vocSource.guideName;
    const displayLabel = formatSource({ ...vocSource, guideName }).label;

    let route = null;
    if (sourceConfig.sourceCategory?.id === 'Surveys') {
        route = { name: 'SurveyDetails', params: { id: objectId } };
    } else if (sourceConfig.id === 'PollResponse' || sourceConfig.id === 'NPS') {
        route = { name: 'GuideDetails', params: { guideId: objectId } };
    }

    return {
        route,
        tooltipContent: displayLabel || '',
    };
}
